import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import GroupHeader from '../common/GroupHeader';
import RecordBadge from '../common/RecordBadge';
import useLinkedSources from '../../hooks/useLinkedSources';
import { api } from '../../api/client';
import { API_ITEM_CARD_COUNTS_URL } from '../../utils';
import { joinMeta } from '../../utils/counts';
import { linkedSourceListPath } from '../../utils/linkedRecords';
import { canSeeLinks, linkedCollectionsPath } from '../../utils/collectionLinks';
import { normalizeId } from '../../utils/ids';

/**
 * LINKED FROM — the collection home's section for what this collection
 * reads from others (keepr-api § Collection links, § Merged lists): one row
 * per source collection, its record badge, what it lends ("Every record", or
 * the cards), how many of its records are in this collection's list, and the
 * door into the merged list narrowed to that source's cards.
 *
 * The counts are the merged list's own (`GET /api/items/counts
 * ?includeLinked=1`, each row's `linkedCount`), summed over the source's
 * borrowed cards. Renders nothing when nothing is read from elsewhere.
 */
export default function LinkedFromSection({ collectionId, collection }) {
	const id = normalizeId(collectionId);
	const enabled = Boolean(id) && canSeeLinks(collection);
	const { sources, borrowedCards, hasIncoming } = useLinkedSources(id, { enabled });

	const [linkedCounts, setLinkedCounts] = useState(null); // cardId -> linkedCount
	useEffect(() => {
		if (!enabled || !hasIncoming) {
			setLinkedCounts(null);
			return undefined;
		}
		let live = true;
		api.get(API_ITEM_CARD_COUNTS_URL, { params: { collection_id: id, includeLinked: 1 } })
			.then(({ data }) => {
				if (!live) return;
				const rows = Array.isArray(data) ? data : [];
				setLinkedCounts(Object.fromEntries(rows.map(row => [normalizeId(row?.card_id), Number(row?.linkedCount) || 0])));
			})
			.catch(() => { if (live) setLinkedCounts(null); });
		return () => { live = false; };
	}, [enabled, hasIncoming, id]);

	const rows = useMemo(() => {
		// Linked records of a card that is not borrowed — a GLOBAL card both
		// collections use is one counts row, not a borrowed card — belong to
		// no source by card id. With one source they are all its own, so they
		// count there; with several the counts cannot say whose they are.
		const borrowedIds = new Set(borrowedCards.map(card => normalizeId(card?._id)).filter(Boolean));
		const unattributed = linkedCounts && sources.size === 1
			? Object.entries(linkedCounts).reduce((sum, [cardId, n]) => (borrowedIds.has(cardId) ? sum : sum + n), 0)
			: 0;
		return [...sources.entries()].map(([sourceId, entry]) => {
			const cards = borrowedCards.filter(card => entry.cardIds.has(normalizeId(card?._id)));
			const whole = entry.links.some(link => !link.card_id && !link.query);
			const count = linkedCounts
				? [...entry.cardIds].reduce((sum, cardId) => sum + (linkedCounts[cardId] || 0), unattributed)
				: null;
			return {
				sourceId,
				source: entry.source || { name: 'Another collection' },
				lends: whole
					? 'Every record'
					: cards.length
						? cards.map(card => card?.name).filter(Boolean).slice(0, 3).join(', ') + (cards.length > 3 ? ` and ${cards.length - 3} more` : '')
						: 'Records matching a filter',
				count,
				path: linkedSourceListPath(id, sourceId, borrowedCards),
			};
		}).sort((a, b) => (a.source?.name || '').localeCompare(b.source?.name || ''));
	}, [sources, borrowedCards, linkedCounts, id]);

	if (!enabled || !rows.length) return null;

	return (
		<section className="collection-home__section" aria-label="Linked from">
			<GroupHeader
				label="Linked from"
				count={rows.length}
				countLabel="collection"
				action={(
					<Link className="collection-home__more" to={linkedCollectionsPath(id)}>
						Linked collections
					</Link>
				)}
			/>
			<div className="collection-home__linked">
				{rows.map(row => (
					<div key={row.sourceId} className="collection-home__linked-row" data-testid="linked-from-row">
						<RecordBadge kind="collection" record={row.source} title={row.source?.name || 'Another collection'} size="sm" />
						<span className="meta-line">
							{joinMeta([
								row.lends,
								typeof row.count === 'number' ? (row.count === 1 ? '1 record here' : `${row.count} records here`) : null,
								'read-only',
							])}
						</span>
						{row.path ? <Link to={row.path}>Show in the list</Link> : null}
					</div>
				))}
			</div>
		</section>
	);
}
